import type { SelfOnboardPayload } from './types';

// Pure sign-up logic for merchant self-onboarding (PE-2097), split out from
// SignUpPage so validation and the request body are unit-testable.
//
// Every rule below mirrors watchman's `SelfOnboardMerchantDto` decorators. The
// server stays authoritative — this exists so a rejection lands next to the
// field instead of as one opaque 400.

export interface SignUpDraft {
  businessName: string;
  companyAddress: string;
  contactFullName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  industryIds: string[];
  businessCategoryIds: string[];
  accountName: string;
  bankCode: string;
  accountNumber: string;
}

export const emptySignUpDraft: SignUpDraft = {
  businessName: '',
  companyAddress: '',
  contactFullName: '',
  contactEmail: '',
  contactPhoneNumber: '',
  industryIds: [],
  businessCategoryIds: [],
  accountName: '',
  bankCode: '',
  accountNumber: '',
};

/** Deliberately permissive — matches class-validator's @IsEmail closely enough to catch typos, not to police RFC 5322. */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateSignUp(draft: SignUpDraft): Record<string, string> {
  const errors: Record<string, string> = {};

  // @MinLength(2) on watchman. Trimmed here because " A " passes a raw length
  // check on the client and then fails server-side after Nest trims nothing —
  // better to reject it where the user can see the field.
  if (draft.businessName.trim().length < 2) errors.businessName = 'Enter your business name.';
  // @MinLength(5)
  if (draft.companyAddress.trim().length < 5) errors.companyAddress = 'Enter your full company address.';
  if (draft.contactFullName.trim().length < 2) errors.contactFullName = 'Enter the contact name.';
  if (!EMAIL.test(draft.contactEmail.trim())) errors.contactEmail = 'Enter a valid email address.';

  // @ArrayMinSize(1) on both — the form cannot be submitted with nothing picked.
  if (!draft.industryIds.length) errors.industryIds = 'Pick at least one industry.';
  if (!draft.businessCategoryIds.length) errors.businessCategoryIds = 'Pick at least one business category.';

  // bankDetails is a required nested object, so all three of its fields are
  // required even though the payouts themselves come much later.
  if (draft.accountName.trim().length < 2) errors.accountName = 'Enter the name on the account.';
  if (!draft.bankCode.trim()) errors.bankCode = 'Enter your bank code.';
  if (!draft.accountNumber.trim()) errors.accountNumber = 'Enter your account number.';

  return errors;
}

/**
 * Build the watchman `SelfOnboardMerchantDto` body.
 *
 * `contactPhoneNumber` is `@IsOptional() @IsString()`, so an empty string would
 * pass validation and be stored as an empty phone number. Omit the key instead
 * of sending `''` — absent and blank are not the same record.
 *
 * `solutionId` is optional and a SLUG here (not the UUID the customer-signup
 * register DTO demands under the same field name). It is only sent when the
 * sign-up link carried one, so a generic sign-up leaves the server to default it.
 */
export function buildSelfOnboardPayload(
  draft: SignUpDraft,
  solutionId?: string | null,
): SelfOnboardPayload {
  const phone = draft.contactPhoneNumber.trim();
  const solution = solutionId?.trim();

  return {
    businessName: draft.businessName.trim(),
    companyAddress: draft.companyAddress.trim(),
    contactFullName: draft.contactFullName.trim(),
    contactEmail: draft.contactEmail.trim(),
    ...(phone ? { contactPhoneNumber: phone } : {}),
    industryIds: draft.industryIds,
    businessCategoryIds: draft.businessCategoryIds,
    ...(solution ? { solutionId: solution } : {}),
    bankDetails: {
      businessName: draft.accountName.trim(),
      bankCode: draft.bankCode.trim(),
      accountNumber: draft.accountNumber.trim(),
    },
  };
}

/** Toggle an id in a multi-select list, preserving order of first selection. */
export function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id];
}
